<script setup lang="ts">
import { ref, onMounted } from 'vue'
import AppModal from '@/components/ui/AppModal.vue'
import { useServicesStore } from '@/domains/home/services/nir/configurations/stores/services.ts'
import type { ServiceGuide, ServiceRequirement, ServiceStep } from '@/domains/home/services/nir/configurations/types/services.ts'
import { dateTimeFormat } from '@/commons/composables/transformations.ts'
import { useConfirmDialog } from '@/composables/useConfirmDialog.ts'

const { showConfirm } = useConfirmDialog()

const store = useServicesStore()

// Modals state
const createServiceModalOpen = ref(false)
const requirementsModalOpen = ref(false)
const stepsModalOpen = ref(false)

const loading = ref(false)
const selectedServiceId = ref('')
const selectedRequirementId = ref('')
const selectedStepId = ref('')

// Forms
const serviceForm = ref({
  name: '',
  description: '',
  overviewDescription: ''
})

const requirementForm = ref({
  description: '',
  position: 0
})

const stepForm = ref({
  title: '',
  description: '',
  position: 0
})

onMounted(() => {
  fetchServices()
})

// --- SERVICES ---
function fetchServices() {
  loading.value = true
  store.fetchServices(1, 100).then(() => {
    loading.value = false
  })
}

function openCreateService() {
  selectedServiceId.value = ''
  serviceForm.value = { name: '', description: '', overviewDescription: '' }
  createServiceModalOpen.value = true
}

function openEditService(service: ServiceGuide) {
  selectedServiceId.value = service.id
  serviceForm.value = {
    name: service.name,
    description: service.description,
    overviewDescription: service.overviewDescription
  }
  createServiceModalOpen.value = true
}

async function deleteService(service: ServiceGuide) {
  const ok = await showConfirm(
    `Are you sure you want to delete "${service.name}"? This action cannot be undone.`,
    { title: 'Delete Service', variant: 'danger', okTitle: 'DELETE' }
  )
  if (!ok) return
  loading.value = true
  store.removeService(service.id).then(() => {
    loading.value = false
    fetchServices()
  }).catch(() => {
    loading.value = false
  })
}

function saveService() {
  loading.value = true
  if (selectedServiceId.value) {
    store.updateService({ id: selectedServiceId.value, ...serviceForm.value }).then(() => {
      createServiceModalOpen.value = false
      fetchServices()
    })
  } else {
    store.createService(serviceForm.value).then(() => {
      createServiceModalOpen.value = false
      fetchServices()
    })
  }
}

// --- REQUIREMENTS ---
function openRequirements(service: ServiceGuide) {
  selectedServiceId.value = service.id
  loading.value = true
  store.fetchRequirements(service.id).then(() => {
    loading.value = false
    requirementsModalOpen.value = true
    resetRequirementForm()
  })
}

function resetRequirementForm() {
  selectedRequirementId.value = ''
  requirementForm.value = { description: '', position: store.requirements?.length || 0 }
}

function editRequirement(req: ServiceRequirement) {
  selectedRequirementId.value = req.id
  requirementForm.value = { description: req.description, position: req.position }
}

function saveRequirement() {
  loading.value = true
  if (selectedRequirementId.value) {
    store.updateRequirement({ id: selectedRequirementId.value, ...requirementForm.value }).then(() => {
      store.fetchRequirements(selectedServiceId.value).then(() => {
        loading.value = false
        resetRequirementForm()
      })
    })
  } else {
    store.createRequirement({ serviceId: selectedServiceId.value, ...requirementForm.value }).then(() => {
      store.fetchRequirements(selectedServiceId.value).then(() => {
        loading.value = false
        resetRequirementForm()
      })
    })
  }
}

// --- STEPS ---
function openSteps(service: ServiceGuide) {
  selectedServiceId.value = service.id
  loading.value = true
  store.fetchSteps(service.id).then(() => {
    loading.value = false
    stepsModalOpen.value = true
    resetStepForm()
  })
}

function resetStepForm() {
  selectedStepId.value = ''
  stepForm.value = { title: '', description: '', position: store.steps?.length || 0 }
}

function editStep(step: ServiceStep) {
  selectedStepId.value = step.id
  stepForm.value = { title: step.title, description: step.description, position: step.position }
}

function saveStep() {
  loading.value = true
  if (selectedStepId.value) {
    store.updateStep({ id: selectedStepId.value, ...stepForm.value }).then(() => {
      store.fetchSteps(selectedServiceId.value).then(() => {
        loading.value = false
        resetStepForm()
      })
    })
  } else {
    store.createStep({ serviceId: selectedServiceId.value, ...stepForm.value }).then(() => {
      store.fetchSteps(selectedServiceId.value).then(() => {
        loading.value = false
        resetStepForm()
      })
    })
  }
}
</script>

<template>
  <div class="flex-1 flex flex-col">
    <!-- Action Bar -->
    <div class="flex items-center justify-end mb-4 pt-4 px-4">
      <button
        @click="openCreateService"
        class="btn-primary bg-primary hover:bg-primary-700 text-white px-4 py-2 rounded text-sm font-medium transition flex items-center space-x-2"
      >
        <i class="fa-solid fa-plus"></i>
        <span>Add Service</span>
      </button>
    </div>

    <!-- Services Table -->
    <div class="bg-white rounded shadow mx-4 flex flex-col">
      <div class="overflow-x-auto flex-1">
        <table class="w-full text-xs">
          <thead class="bg-gray-50 sticky top-0">
            <tr>
              <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200 w-12">#</th>
              <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Name</th>
              <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Description</th>
              <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Created At</th>
              <th class="text-center py-3 px-4 font-semibold text-gray-700 border-b border-gray-200 w-48">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(service, idx) in store.services" :key="service.id" class="hover:bg-gray-50 transition">
              <td class="py-3 px-4 border-b border-gray-200 font-semibold">{{ idx + 1 }}</td>
              <td class="py-3 px-4 border-b border-gray-200 font-semibold text-primary">{{ service.name }}</td>
              <td class="py-3 px-4 border-b border-gray-200">{{ service.description }}</td>
              <td class="py-3 px-4 border-b border-gray-200">{{ service.createdAt ? dateTimeFormat(service.createdAt) : '--' }}</td>
              <td class="py-3 px-4 border-b border-gray-200 text-center">
                <div class="flex items-center justify-center space-x-2">
                  <button @click="openRequirements(service)" class="btn-sm-primary bg-sky-100 text-sky-700 hover:bg-sky-200 px-2 py-1 rounded text-xs" title="Requirements">
                    <i class="fa-solid fa-list-check"></i>
                  </button>
                  <button @click="openSteps(service)" class="btn-sm-primary bg-amber-100 text-amber-700 hover:bg-amber-200 px-2 py-1 rounded text-xs" title="Steps">
                    <i class="fa-solid fa-shoe-prints"></i>
                  </button>
                  <button @click="openEditService(service)" class="btn-sm-primary bg-gray-100 text-gray-700 hover:bg-gray-200 px-2 py-1 rounded text-xs" title="Edit">
                    <i class="fa-solid fa-pen"></i>
                  </button>
                  <button @click="deleteService(service)" class="btn-sm-primary bg-red-100 text-red-700 hover:bg-red-200 px-2 py-1 rounded text-xs" title="Delete">
                    <i class="fa-solid fa-trash"></i>
                  </button>
                </div>
              </td>
            </tr>
            <tr v-if="!store.services || store.services.length === 0">
              <td colspan="5" class="py-4 text-center text-gray-500">No services found.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Create/Edit Service Modal -->
    <AppModal v-model="createServiceModalOpen" xl2>
      <div class="p-4">
        <h2 class="text-lg font-bold text-gray-900 mb-4">{{ selectedServiceId ? 'Edit' : 'Add' }} Service Guide</h2>
        <form @submit.prevent="saveService" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1.5">Name *</label>
            <input v-model="serviceForm.name" type="text" required class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary" placeholder="Service Name" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1.5">Description *</label>
            <textarea v-model="serviceForm.description" required class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary" rows="2" placeholder="Short description"></textarea>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1.5">Overview Description *</label>
            <textarea v-model="serviceForm.overviewDescription" required class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary" rows="4" placeholder="Detailed overview"></textarea>
          </div>
          <div class="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
            <button type="button" @click="createServiceModalOpen = false" class="btn-danger">Cancel</button>
            <button type="submit" :disabled="loading" class="btn-primary px-4 py-2 bg-primary hover:bg-primary-700 text-white rounded text-sm font-medium disabled:opacity-50">
              <i class="fa-solid fa-check mr-1"></i> {{ loading ? 'Saving...' : 'Save Service' }}
            </button>
          </div>
        </form>
      </div>
    </AppModal>

    <!-- Requirements Modal -->
    <AppModal v-model="requirementsModalOpen" xl2>
      <div class="p-4">
        <h2 class="text-lg font-bold text-gray-900 mb-4">Manage Requirements</h2>
        <form @submit.prevent="saveRequirement" class="space-y-4 mb-6 bg-gray-50 p-4 rounded border border-gray-200">
          <h3 class="text-sm font-semibold mb-2">{{ selectedRequirementId ? 'Edit' : 'Add' }} Requirement</h3>
          <div class="grid grid-cols-4 gap-4">
            <div class="col-span-3">
              <label class="block text-xs font-medium text-gray-700 mb-1">Description *</label>
              <input v-model="requirementForm.description" type="text" required class="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary" />
            </div>
            <div class="col-span-1">
              <label class="block text-xs font-medium text-gray-700 mb-1">Position *</label>
              <input v-model.number="requirementForm.position" type="number" required min="0" class="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary" />
            </div>
          </div>
          <div class="flex justify-end space-x-2 pt-2">
            <button v-if="selectedRequirementId" type="button" @click="resetRequirementForm" class="btn-danger text-xs px-3 py-1.5">Cancel Edit</button>
            <button type="submit" :disabled="loading" class="bg-primary hover:bg-primary-700 text-white text-xs font-medium px-3 py-1.5 rounded disabled:opacity-50">
              {{ selectedRequirementId ? 'Update' : 'Add' }} Requirement
            </button>
          </div>
        </form>

        <table class="w-full text-xs">
          <thead class="bg-gray-100">
            <tr>
              <th class="text-left py-2 px-3 font-semibold border-b w-12">Pos</th>
              <th class="text-left py-2 px-3 font-semibold border-b">Description</th>
              <th class="text-center py-2 px-3 font-semibold border-b w-24">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="req in store.requirements" :key="req.id" class="border-b">
              <td class="py-2 px-3">{{ req.position }}</td>
              <td class="py-2 px-3">{{ req.description }}</td>
              <td class="py-2 px-3 text-center">
                <button @click="editRequirement(req)" class="text-primary hover:text-primary-700"><i class="fa-solid fa-pen"></i></button>
              </td>
            </tr>
            <tr v-if="!store.requirements || store.requirements.length === 0">
              <td colspan="3" class="py-4 text-center text-gray-500">No requirements found.</td>
            </tr>
          </tbody>
        </table>
        <div class="mt-4 flex justify-end">
          <button @click="requirementsModalOpen = false" class="btn-danger">Close</button>
        </div>
      </div>
    </AppModal>

    <!-- Steps Modal -->
    <AppModal v-model="stepsModalOpen" xl2>
      <div class="p-4">
        <h2 class="text-lg font-bold text-gray-900 mb-4">Manage Steps</h2>
        <form @submit.prevent="saveStep" class="space-y-4 mb-6 bg-gray-50 p-4 rounded border border-gray-200">
          <h3 class="text-sm font-semibold mb-2">{{ selectedStepId ? 'Edit' : 'Add' }} Step</h3>
          <div class="grid grid-cols-4 gap-4">
            <div class="col-span-1">
              <label class="block text-xs font-medium text-gray-700 mb-1">Position *</label>
              <input v-model.number="stepForm.position" type="number" required min="0" class="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary" />
            </div>
            <div class="col-span-3">
              <label class="block text-xs font-medium text-gray-700 mb-1">Title *</label>
              <input v-model="stepForm.title" type="text" required class="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary" />
            </div>
            <div class="col-span-4">
              <label class="block text-xs font-medium text-gray-700 mb-1">Description *</label>
              <textarea v-model="stepForm.description" required rows="2" class="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary"></textarea>
            </div>
          </div>
          <div class="flex justify-end space-x-2 pt-2">
            <button v-if="selectedStepId" type="button" @click="resetStepForm" class="btn-danger text-xs px-3 py-1.5">Cancel Edit</button>
            <button type="submit" :disabled="loading" class="bg-primary hover:bg-primary-700 text-white text-xs font-medium px-3 py-1.5 rounded disabled:opacity-50">
              {{ selectedStepId ? 'Update' : 'Add' }} Step
            </button>
          </div>
        </form>

        <table class="w-full text-xs">
          <thead class="bg-gray-100">
            <tr>
              <th class="text-left py-2 px-3 font-semibold border-b w-12">Pos</th>
              <th class="text-left py-2 px-3 font-semibold border-b">Title</th>
              <th class="text-left py-2 px-3 font-semibold border-b">Description</th>
              <th class="text-center py-2 px-3 font-semibold border-b w-24">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="step in store.steps" :key="step.id" class="border-b">
              <td class="py-2 px-3">{{ step.position }}</td>
              <td class="py-2 px-3 font-semibold">{{ step.title }}</td>
              <td class="py-2 px-3">{{ step.description }}</td>
              <td class="py-2 px-3 text-center">
                <button @click="editStep(step)" class="text-primary hover:text-primary-700"><i class="fa-solid fa-pen"></i></button>
              </td>
            </tr>
            <tr v-if="!store.steps || store.steps.length === 0">
              <td colspan="4" class="py-4 text-center text-gray-500">No steps found.</td>
            </tr>
          </tbody>
        </table>
        <div class="mt-4 flex justify-end">
          <button @click="stepsModalOpen = false" class="btn-danger">Close</button>
        </div>
      </div>
    </AppModal>
  </div>
</template>

<style scoped>
table {
  border-collapse: separate;
  border-spacing: 0;
}
</style>
